import React, { memo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

const MAX_DOTS = 2200;
const MAX_PIXEL_COUNT = 1800000;
const MOTION_HOLD_MS = 120;
const MOTION_EXIT_MS = 670;

const clamp = (value, minimum, maximum) => Math.min(maximum, Math.max(minimum, value));

const colorToRgb = (color) => {
  const normalized = String(color || '').trim().replace(/^#/, '');
  const expanded = normalized.length === 3
    ? normalized.split('').map((character) => `${character}${character}`).join('')
    : normalized;
  const value = Number.parseInt(expanded, 16);

  if (!Number.isFinite(value) || expanded.length !== 6) {
    return { red: 99, green: 102, blue: 241 };
  }

  return {
    red: (value >> 16) & 255,
    green: (value >> 8) & 255,
    blue: value & 255
  };
};

const pointPhase = (column, row) => {
  const raw = Math.sin(column * 12.9898 + row * 78.233) * 43758.5453;
  return (raw - Math.floor(raw)) * Math.PI * 2;
};

const InteractiveDotGridCanvas = memo(({
  color = '#6366f1',
  dotSize = 2.4,
  gap = 29,
  effectRadius = 126,
  activeScale = 2.1,
  orbitSpeed = 1.5
}) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const context = canvas.getContext('2d', { alpha: true });
    if (!context) return undefined;

    const rgb = colorToRgb(color);
    const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const finePointerQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
    const pointer = { x: -10000, y: -10000, lastMoveAt: Number.NEGATIVE_INFINITY };
    let width = 1;
    let height = 1;
    let renderGap = gap;
    let pixelRatio = 1;
    let animationFrame = 0;
    let resizeObserver = null;
    let disposed = false;

    const interactionEnabled = () => (
      !reducedMotionQuery.matches
      && finePointerQuery.matches
      && !document.hidden
    );

    const draw = (timestamp = performance.now()) => {
      if (disposed) return;

      context.setTransform(1, 0, 0, 1, 0, 0);
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);

      const elapsed = timestamp - pointer.lastMoveAt;
      const motionStrength = interactionEnabled()
        ? clamp(1 - Math.max(0, elapsed - MOTION_HOLD_MS) / MOTION_EXIT_MS, 0, 1)
        : 0;
      const columns = Math.ceil(width / renderGap) + 1;
      const rows = Math.ceil(height / renderGap) + 1;
      const startX = (width - (columns - 1) * renderGap) / 2;
      const startY = (height - (rows - 1) * renderGap) / 2;
      const baseRadius = Math.max(0.8, dotSize / 2);

      context.beginPath();
      for (let row = 0; row < rows; row += 1) {
        const y = startY + row * renderGap;
        for (let column = 0; column < columns; column += 1) {
          const x = startX + column * renderGap;
          const distanceX = x - pointer.x;
          const distanceY = y - pointer.y;
          const distance = Math.hypot(distanceX, distanceY);
          const proximity = motionStrength * clamp(1 - distance / effectRadius, 0, 1);

          if (proximity > 0.012) continue;
          context.moveTo(x + baseRadius, y);
          context.arc(x, y, baseRadius, 0, Math.PI * 2);
        }
      }
      context.fillStyle = `rgba(${rgb.red}, ${rgb.green}, ${rgb.blue}, 0.2)`;
      context.fill();

      if (motionStrength > 0) {
        for (let row = 0; row < rows; row += 1) {
          const y = startY + row * renderGap;
          for (let column = 0; column < columns; column += 1) {
            const x = startX + column * renderGap;
            const distanceX = x - pointer.x;
            const distanceY = y - pointer.y;
            const distance = Math.hypot(distanceX, distanceY);
            const proximity = motionStrength * clamp(1 - distance / effectRadius, 0, 1);
            if (proximity <= 0.012) continue;

            const easedProximity = proximity * proximity * (3 - 2 * proximity);
            const phase = pointPhase(column, row);
            const orbitAngle = timestamp * 0.001 * orbitSpeed + phase;
            const orbitRadius = 11 * easedProximity;
            const tilt = 0.3 + 0.34 * Math.sin(phase * 0.73);
            const orbitX = Math.cos(orbitAngle) * orbitRadius;
            const orbitY = Math.sin(orbitAngle) * orbitRadius * tilt;
            const radialPush = 4.5 * easedProximity;
            const normalX = distance > 0 ? distanceX / distance : 0;
            const normalY = distance > 0 ? distanceY / distance : 0;
            const drawX = x + orbitX + normalX * radialPush;
            const drawY = y + orbitY + normalY * radialPush;
            const radius = baseRadius * (1 + easedProximity * (activeScale - 1));

            context.beginPath();
            context.arc(drawX, drawY, radius + 6 * easedProximity, 0, Math.PI * 2);
            context.fillStyle = `rgba(${rgb.red}, ${rgb.green}, ${rgb.blue}, ${0.09 * easedProximity})`;
            context.fill();

            context.beginPath();
            context.arc(drawX, drawY, radius, 0, Math.PI * 2);
            context.fillStyle = `rgba(${rgb.red}, ${rgb.green}, ${rgb.blue}, ${0.3 + 0.68 * easedProximity})`;
            context.fill();
          }
        }
      }

      if (elapsed < MOTION_HOLD_MS + MOTION_EXIT_MS && interactionEnabled()) {
        canvas.dataset.activity = 'active';
        animationFrame = window.requestAnimationFrame(draw);
      } else {
        canvas.dataset.activity = 'idle';
        animationFrame = 0;
      }
    };

    const requestDraw = () => {
      if (animationFrame || disposed) return;
      animationFrame = window.requestAnimationFrame(draw);
    };

    const resizeCanvas = () => {
      const bounds = canvas.getBoundingClientRect();
      width = Math.max(1, bounds.width);
      height = Math.max(1, bounds.height);
      const requestedPixelRatio = Math.min(window.devicePixelRatio || 1, 1.5);
      const pixelCountLimit = Math.sqrt(MAX_PIXEL_COUNT / Math.max(1, width * height));
      pixelRatio = Math.max(1, Math.min(requestedPixelRatio, pixelCountLimit));
      const estimatedDotCount = (width / gap + 2) * (height / gap + 2);
      renderGap = gap * Math.max(1, Math.sqrt(estimatedDotCount / MAX_DOTS));
      canvas.width = Math.max(1, Math.round(width * pixelRatio));
      canvas.height = Math.max(1, Math.round(height * pixelRatio));
      canvas.dataset.pixelRatio = pixelRatio.toFixed(2);
      draw();
    };

    const handlePointerMove = (event) => {
      if (!interactionEnabled() || (event.pointerType && event.pointerType !== 'mouse')) return;
      const bounds = canvas.getBoundingClientRect();
      pointer.x = event.clientX - bounds.left;
      pointer.y = event.clientY - bounds.top;
      pointer.lastMoveAt = performance.now();
      requestDraw();
    };

    const handleInteractionPreferenceChange = () => {
      pointer.lastMoveAt = Number.NEGATIVE_INFINITY;
      canvas.dataset.motion = interactionEnabled() ? 'interactive' : 'static';
      if (animationFrame) {
        window.cancelAnimationFrame(animationFrame);
        animationFrame = 0;
      }
      draw();
    };

    const handleVisibilityChange = () => {
      handleInteractionPreferenceChange();
    };

    canvas.dataset.motion = interactionEnabled() ? 'interactive' : 'static';
    window.addEventListener('pointermove', handlePointerMove, { passive: true });
    document.addEventListener('visibilitychange', handleVisibilityChange);
    reducedMotionQuery.addEventListener?.('change', handleInteractionPreferenceChange);
    finePointerQuery.addEventListener?.('change', handleInteractionPreferenceChange);

    if ('ResizeObserver' in window) {
      resizeObserver = new window.ResizeObserver(resizeCanvas);
      resizeObserver.observe(canvas);
    } else {
      window.addEventListener('resize', resizeCanvas, { passive: true });
    }
    resizeCanvas();

    return () => {
      disposed = true;
      if (animationFrame) window.cancelAnimationFrame(animationFrame);
      resizeObserver?.disconnect();
      window.removeEventListener('resize', resizeCanvas);
      window.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      reducedMotionQuery.removeEventListener?.('change', handleInteractionPreferenceChange);
      finePointerQuery.removeEventListener?.('change', handleInteractionPreferenceChange);
    };
  }, [activeScale, color, dotSize, effectRadius, gap, orbitSpeed]);

  return (
    <canvas
      ref={canvasRef}
      className="stocktag-loading-screen__dot-grid"
      data-loading-dot-grid="true"
      data-dot-color={color}
      aria-hidden="true"
    />
  );
});

InteractiveDotGridCanvas.displayName = 'InteractiveDotGridCanvas';

InteractiveDotGridCanvas.propTypes = {
  color: PropTypes.string,
  dotSize: PropTypes.number,
  gap: PropTypes.number,
  effectRadius: PropTypes.number,
  activeScale: PropTypes.number,
  orbitSpeed: PropTypes.number
};

export default InteractiveDotGridCanvas;
